import { useCallback, useSyncExternalStore } from 'react';
import { useOptionalSession, useSession } from '../../data/hooks.tsx';
import { WindowManager, type WindowSource, type WindowsState } from './windows.ts';

const managers = new Map<string, WindowManager>();

/** The window manager of a project (one per project for the life of the app). */
export function windowManager(projectId: string): WindowManager {
  let manager = managers.get(projectId);
  if (!manager) managers.set(projectId, (manager = new WindowManager(`tomo.windows.${projectId}`)));
  return manager;
}

export const useWindowManager = (): WindowManager => windowManager(useSession().id);

export function useWindows(): WindowsState {
  const manager = useWindowManager();
  return useSyncExternalStore(manager.subscribe, manager.get, manager.get);
}

/** One value derived from the windows; re-renders only when it changes (return primitives). */
export function useWindowsValue<T>(select: (s: WindowsState) => T): T {
  const manager = useWindowManager();
  const get = () => select(manager.get());
  return useSyncExternalStore(manager.subscribe, get, get);
}

/** Opens a record in a window, or brings its window to the front. */
export function useOpenRecord(): (recordId: string, from?: WindowSource | null) => void {
  const session = useOptionalSession();
  return useCallback(
    (recordId: string, from: WindowSource | null = null) => {
      if (session) windowManager(session.id).open(recordId, from);
    },
    [session],
  );
}
